import React from "react"
import styled from "styled-components"
import {
  Copyright,
  Header, HeroImage,
  HR,
  LinkText, LoginButton,
  Main,
  MainInnerWrap, PageTitle,
  Paragraph,
  People,
  Person, PersonSubTitle, PersonTitle,
  Portrait, Row,
  Section,
  SectionTitle
} from "../Landing";

// 1) Grid wrapper
const HeroGrid = styled.div`
  display: grid;
  gap: 3.75rem;            /* matches your HeroImage bottom margin */
  
  /* mobile: one column */
  grid-template-columns: 1fr;

  /* desktop: two equal columns */
  @media (min-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

// 2) Optional: remove bottom margin because grid gap already gives spacing
const SideHeroImage = styled(HeroImage)`
  margin-bottom: 0;
`

export default function SightLanding() {
  return (
    <Main>
      <MainInnerWrap>
        <Header>
          <Row>
            <PageTitle>
              Michigan Sight Program
            </PageTitle>
          </Row>
          <LoginButton href="/login">Login Implementation Toolkit</LoginButton>
        </Header>
        <HeroGrid>
          <HeroImage src="/images/eye-exam-sight-1.jpg" />
          <HeroImage src="/images/eye-exam-sight-2.jpg" />
        </HeroGrid>
        <Section>
          <SectionTitle>What is the MI SIGHT Program?</SectionTitle>
          <Paragraph>To preserve sight as we age, people need to get their eyes checked for eye disease so they can get good treatments if they are needed. And yet, many people have a hard time getting in to see an eye doctor. The Michigan Screening and Intervention for Glaucoma and Eye Health through Telemedicine (MI-SIGHT) Program uses a technology-based eye disease screening protocol that is reviewed by an eye doctor with expertise in telemedicine to identify people who need further care for eye diseases. The MI-SIGHT Program also helps people use on-line glasses retailers to buy inexpensive, fashionable glasses to get affordable glasses.</Paragraph>
        </Section>
        <Section>
          <SectionTitle>What is the MI SIGHT Program Trial?</SectionTitle>
          <Paragraph>To understand whether the MI-SIGHT Program improves glaucoma and eye disease detection, we will compare the MI-SIGHT Program to a standard comprehensive examination with an eye doctor in a randomized controlled trial. A randomized controlled trial is a study in which people are allocated randomly (by chance alone) to receive a new intervention or the standard intervention. This is the best research method to understand whether a new intervention is more effective compared to the standard intervention. After the completion of the MI-SIGHT Program randomized controlled trial, we will know whether to recommend the MI-SIGHT Program as the new “standard of care” to policymakers for glaucoma and eye disease screening. To find out more about the trial, visit <LinkText href="https://clinicaltrials.gov/ct2/show/NCT04735653?cond=glaucoma&cntry=US&state=US%3AMI&draw=2&rank=3">ClinicalTrials.gov</LinkText>.</Paragraph>
        </Section>
        <Section>
          <SectionTitle>Why participate in the MI SIGHT Program Trial?</SectionTitle>
          <Paragraph>Participating in research can help you and gives you an opportunity to help many others. Participants in the MI SIGHT Program trial will receive both a comprehensive eye exam and a technology-based eye exam to best detect any eye diseases. By participating in the trial, you will help researchers and health policy makers understand whether paying for technology-based eye disease screening would help more people know that they have glaucoma or other eye diseases earlier so they can get treatments before the diseases cause vision loss.</Paragraph>
        </Section>
        <Section>
          <SectionTitle>MI SIGHT Program Publications and Presentations</SectionTitle>
          <Paragraph><LinkText href="/publications" target="_blank">Click to see the SIGHT Program Publications</LinkText></Paragraph>
        </Section>
        <Section>
          <SectionTitle>How can I participate in the MI SIGHT Program Trial?</SectionTitle>
          <Paragraph>To participate in the program, please let us know by contacting one of the study locations below.</Paragraph>
        </Section>
        <HR/>
        <Section>
          <SectionTitle>Study Location</SectionTitle>
        </Section>
        <Section>
          <SectionTitle>Our Team</SectionTitle>
          <People>
            <Person>
              <Portrait src="/images/people/PANC-sight.png" />
              <PersonTitle>Paula Anne Newman-Casey, MD, MS</PersonTitle>
              <PersonSubTitle>Associate Professor of Ophthalmology and Visual Sciences</PersonSubTitle>
              <PersonSubTitle>Strategic Director, Kellogg Eye Center for eHealth</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Woodward-sight.jpg" />
              <PersonTitle>Maria Woodward, MD, MSc</PersonTitle>
              <PersonSubTitle>Associate Professor of Ophthalmology and Visual Sciences</PersonSubTitle>
              <PersonSubTitle>Strategic Director, Kellogg Eye Center for eHealth</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Angela-sight.jpg" />
              <PersonTitle>Angela R. Elam, MD</PersonTitle>
              <PersonSubTitle>Assistant Professor of Ophthalmology and Visual Sciences</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Winter-sight.png" />
              <PersonTitle>Suzanne Winter, MS, CCRC, ACRP-PM </PersonTitle>
              <PersonSubTitle>Clinical Research Project Manager</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Thy-sight.jpg" />
              <PersonTitle>Thy Nguyen, BS</PersonTitle>
              <PersonSubTitle>Research Administrative Associate</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Jade-sight.png" />
              <PersonTitle>Jade Livingston, BS</PersonTitle>
              <PersonSubTitle>Clinical Research Coordinator - Technician</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/Mildred-sight.jpg" />
              <PersonTitle>Mildred Silva-Zuccaro, MBBS</PersonTitle>
              <PersonSubTitle>Clinical Subjects Coordinator</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Phalatha-sight.jpg" />
              <PersonTitle>Phalatha McHaney-Conner, BS, CCRC</PersonTitle>
              <PersonSubTitle>Clinical Research Coordinator - Intermediate</PersonSubTitle>
            </Person>
            <Person>
              <Portrait src="/images/people/Asia-sight.jpg" />
              <PersonTitle>Asia Priester</PersonTitle>
              <PersonSubTitle>Clinical Research Coordinator - Technician</PersonSubTitle>
            </Person>
          </People>
          <People>
            <Person>
              <Portrait src="/images/people/David-sight.jpg" />
              <PersonTitle>David Bush, OD</PersonTitle>
              <PersonSubTitle>Optometrist - Hamilton Community Health Network</PersonSubTitle>
            </Person>
          </People>
        </Section>
        <HR/>
        <Section>
          <SectionTitle fontSize="2rem">The Vision Health Initiative</SectionTitle>
          <Paragraph>
            <LinkText href="https://www.cdc.gov/vision-health/" target="_blank">The Vision Health Initiative (VHI)</LinkText> at the Centers for Disease Control and Prevention aims to enhance public health surveillance and research that provides the basis for effective public health programs and policies to reduce the burden of vision loss. Estimates of the U.S. economic burden of vision loss nationally and by state can be examined on CDC’s <LinkText href="https://www.cdc.gov/vision-health/php/economics-explorer/index.html" target="_blank">Vision Loss Economic Explorer</LinkText>. The MI-SIGHT Trial is funded through the Vision Health Initiative.
          </Paragraph>
        </Section>
        <Copyright>© 2025 The University of Michigan – All Rights Reserved</Copyright>
      </MainInnerWrap>
    </Main>
  )
}


// WEBPACK FOOTER //
// ./src/pages/SightLanding/index.js